import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TumblerPaywall.module.scss"

/**
 * Tumbler — a FEATURE-SPECIFIC paywall whose job, per the active Slack standing
 * directive ("sell the ultimate subscription plan, default to ultimate but
 * allow buying premium as well"), is to sell the *Ultimate* tier over Premium.
 *
 * The mechanic is a **rotary combination dial** — the first rotary interaction
 * in the whole set (prior Ultimate-sellers used a vertical lever, Trove, and a
 * parallax horizon, Vista; neither turned). Your vault sits at the centre of a
 * fine, light "instrument". Around its rim are eight **tumblers**, one per power.
 * Turn the dial toward ULTIMATE and each Ultimate-only tumbler *seats* with a
 * click as the index sweeps past it, a glowing violet **ring completes** around
 * the whole face, the capacity readout counts 50 GB → 1 TB and the centre clasp
 * goes from a single lock to a dual lock. Turn back to PREMIUM and the five
 * Ultimate tumblers honestly un-seat to faint outline (room Premium doesn't
 * include) while the three core tumblers — private vault, auto-backup, locked to
 * you — stay solid. Nothing is ever deleted; the dial only chooses how much of
 * the instrument you arm.
 *
 * Divergence (vs. the ledger): value frame is **completion — "the whole set,
 * fully kept"** (goal-gradient / Zeigarnik, honest — untouched by all priors).
 * Interaction is **rotary** (unused). Layout is **radial / clock-face** (unused).
 * Art direction is a **light horology instrument** — warm platinum, guilloché
 * rings, brand blue core + Ultimate violet halo — deliberately not the dark,
 * glowing-object cluster. Register: **calm precision / quiet mastery.**
 *
 * Self-contained: renders on the built-in debug product data (Ultimate yearly
 * $119.99 / $9.99-per-mo · 7-day trial; Premium yearly $35.99), reuses the
 * bundled `nimbus/` photos, no native injection. Yearly is the featured, default
 * price for whichever tier the dial selects; monthly is demoted to one small line.
 */

type Tier = "ultimate" | "premium"

// Real photos from the bundled set — the memories inside the vault well.
const WELL_PHOTOS = [
  "mem3.jpg",
  "mem8.jpg",
  "mem1.jpg",
  "mem12.jpg",
  "mem6.jpg",
  "mem15.jpg",
  "mem9.jpg",
]

// The eight tumblers on the rim, clockwise from the top index.
// `ultimate: false` are core powers Premium keeps too; the rest are the
// Ultimate-only powers that seat as you turn toward Ultimate.
interface Tumbler {
  label: string
  ultimate: boolean
  // fraction of the turn (0..1) at which this tumbler clicks into place
  seatAt: number
}
const TUMBLERS: Tumbler[] = [
  { label: "1 TB storage", ultimate: true, seatAt: 0.16 },
  { label: "Smart Albums", ultimate: true, seatAt: 0.32 },
  { label: "Instant Search", ultimate: true, seatAt: 0.48 },
  { label: "Private vault", ultimate: false, seatAt: 0 },
  { label: "Auto-backup", ultimate: false, seatAt: 0 },
  { label: "Locked to you", ultimate: false, seatAt: 0 },
  { label: "Full-res originals", ultimate: true, seatAt: 0.64 },
  { label: "Duplicates", ultimate: true, seatAt: 0.8 },
]

const GB_PREMIUM = 50
const GB_ULTIMATE = 1000

// Dial geometry (as % of the square dial box).
const PIN_RADIUS = 43

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

const Glyph = ({
  name,
}: {
  name: "lock" | "arrow" | "close" | "check" | "spark"
}) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "spark":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2.5c.6 4.7 2.3 6.4 7 7-4.7.6-6.4 2.3-7 7-.6-4.7-2.3-6.4-7-7 4.7-.6 6.4-2.3 7-7Z"
            fill="currentColor"
          />
        </svg>
      )
  }
}

const TumblerPaywall = () => {
  const { state, actions } = useAppState()

  const ultimate = state.productData?.ultimate
  const premium = state.productData?.premium
  const locale =
    ultimate?.yearlyProduct?.priceLocale ?? premium?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null ? formatCurrency(v, locale) : fallback

  const plans = useMemo(
    () => ({
      ultimate: {
        yearly: ultimate?.yearlyProduct?.price ?? 119.99,
        monthlyPrice: ultimate?.monthlyProduct?.price ?? 19.99,
        product: ultimate?.yearlyProduct,
        monthlyProduct: ultimate?.monthlyProduct,
        hasTrial: ultimate?.yearlyProduct?.hasTrial ?? true,
        trialDays: ultimate?.yearlyProduct?.introPricing?.period ?? 7,
      },
      premium: {
        yearly: premium?.yearlyProduct?.price ?? 35.99,
        monthlyPrice: premium?.monthlyProduct?.price ?? 13.99,
        product: premium?.yearlyProduct,
        monthlyProduct: premium?.monthlyProduct,
        hasTrial: premium?.yearlyProduct?.hasTrial ?? true,
        trialDays: premium?.yearlyProduct?.introPricing?.period ?? 7,
      },
    }),
    [ultimate, premium],
  )

  const [tier, setTier] = useState<Tier>("ultimate")
  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")

  // `seat` is the single source of truth for the whole instrument: 0 = Premium,
  // 1 = Ultimate. Every visual (dial rotation, ring completion, capacity, glow)
  // derives from it, so a mid-turn drag animates everything in lockstep.
  const [seat, setSeat] = useState(1)
  const seatRef = useRef(1)
  const rafRef = useRef(0)
  const draggingRef = useRef(false)

  const writeSeat = (v: number) => {
    seatRef.current = v
    setSeat(v)
  }

  const animateSeatTo = useCallback((target: number, dur = 620) => {
    cancelAnimationFrame(rafRef.current)
    const from = seatRef.current
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur)
      writeSeat(lerp(from, target, easeOutCubic(t)))
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [])

  const selectTier = useCallback(
    (t: Tier) => {
      setTier(t)
      animateSeatTo(t === "ultimate" ? 1 : 0)
    },
    [animateSeatTo],
  )

  // Tint the top OS inset to the instrument's warm platinum ground. The page's
  // own light gradient runs edge-to-edge, so we drop the footer inset band
  // rather than let it overlay the legal row.
  useEffect(() => {
    actions.setHeaderInsetColor("#efe9e0")
    actions.setIgnoreFooterInset(true)
  }, [actions])

  // Auto-demo once on load: start seated at Ultimate, unwind to Premium to
  // reveal what turning arms, then snap back to the featured Ultimate.
  useEffect(() => {
    writeSeat(1)
    const t1 = setTimeout(() => animateSeatTo(0.06, 560), 560)
    const t2 = setTimeout(() => animateSeatTo(1, 900), 1320)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      cancelAnimationFrame(rafRef.current)
    }
  }, [animateSeatTo])

  // --- Rotary drag on the dial -----------------------------------------
  const dialRef = useRef<HTMLDivElement>(null)

  const seatFromPointer = useCallback((clientX: number, clientY: number) => {
    const el = dialRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    // Angle of the pointer around the centre, degrees. 0 = east, +down.
    const deg = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI
    // The index rides the top arc: north-west (-135°) = Premium (0),
    // north-east (-45°) = Ultimate (1). Clamp keeps the lower half stable.
    const s = clamp01((deg - -135) / 90)
    writeSeat(s)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      cancelAnimationFrame(rafRef.current)
      draggingRef.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      seatFromPointer(e.clientX, e.clientY)
    },
    [seatFromPointer],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      seatFromPointer(e.clientX, e.clientY)
    },
    [seatFromPointer],
  )
  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      draggingRef.current = false
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
      // Snap to the nearer detent.
      const t: Tier = seatRef.current >= 0.5 ? "ultimate" : "premium"
      selectTier(t)
    },
    [selectTier],
  )
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        selectTier("ultimate")
        e.preventDefault()
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        selectTier("premium")
        e.preventDefault()
      }
    },
    [selectTier],
  )

  // --- Derived visuals --------------------------------------------------
  const isUlt = tier === "ultimate"
  const eased = easeOutCubic(clamp01(seat))
  const gb = Math.round(lerp(GB_PREMIUM, GB_ULTIMATE, eased))
  const gbLabel = gb >= 1000 ? "1 TB" : `${gb} GB`
  // Dial + index rotation: a 44° sweep between the two detents.
  const dialAngle = lerp(-44, 0, seat)
  // How many tumblers are currently seated (for the readout).
  const seatedCount = TUMBLERS.filter(
    (t) => !t.ultimate || seat >= t.seatAt + 0.001,
  ).length

  // Pricing — yearly featured for whichever tier the dial selects.
  const active = plans[tier]
  const yearlyPrice = money(active.yearly, isUlt ? "$119.99" : "$35.99")
  const monthlyOfYear = money(active.yearly / 12, isUlt ? "$9.99" : "$2.99")
  const monthlyPrice = money(active.monthlyPrice, isUlt ? "$19.99" : "$13.99")
  const savePercent = Math.max(
    0,
    Math.round((1 - active.yearly / (active.monthlyPrice * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? active.product : active.monthlyProduct
  const selectedPriceLine =
    billing === "yearly"
      ? active.hasTrial
        ? `${active.trialDays} days free, then ${yearlyPrice} per year`
        : `${yearlyPrice} per year`
      : `${monthlyPrice} per month`
  const ctaLabel =
    billing === "yearly" && active.hasTrial
      ? `Start ${active.trialDays} days free`
      : isUlt
        ? "Get Ultimate"
        : "Get Premium"

  const buy = useCallback(() => {
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [selectedProduct])
  const close = useCallback(() => native.postNativeEvent("close"), [])

  // Progress ring geometry (SVG, r=46 in a 100 viewBox → circumference).
  const RING_R = 46
  const RING_C = 2 * Math.PI * RING_R
  const ringDash = RING_C * eased

  return (
    <div
      className={styles.page}
      data-tier={tier}
      style={{ "--seat": eased } as React.CSSProperties}
    >
      <div className={styles.aura} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Glyph name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Glyph name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Turn it all the way{" "}
          <span className={styles.headlineSwap}>
            <span className={styles.wordUlt}>up.</span>
            <span className={styles.wordPrem}>down.</span>
          </span>
        </h1>
        <p className={styles.sub}>
          Every photo and video, backed up and locked to you. Turn the dial to
          arm the full set.
        </p>
      </header>

      {/* The instrument. */}
      <section className={styles.stage}>
        <div
          className={styles.dial}
          ref={dialRef}
          role="slider"
          aria-label="Choose your plan tier by turning the dial"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={isUlt ? 1 : 0}
          aria-valuetext={isUlt ? "Ultimate — full set" : "Premium — core set"}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {/* Guilloché + progress ring. */}
          <svg
            className={styles.rings}
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <circle className={styles.ringTrack} cx="50" cy="50" r={RING_R} />
            <circle
              className={styles.ringFill}
              cx="50"
              cy="50"
              r={RING_R}
              strokeDasharray={`${ringDash} ${RING_C}`}
              transform="rotate(-90 50 50)"
            />
            <circle className={styles.ringInner} cx="50" cy="50" r="38" />
          </svg>

          {/* The rotating face: index + tumbler pins turn together. */}
          <div
            className={styles.face}
            style={{ transform: `rotate(${dialAngle}deg)` }}
          >
            <span className={styles.index} aria-hidden="true" />
            {TUMBLERS.map((t, i) => {
              const angle = -90 + (i * 360) / TUMBLERS.length
              const rad = (angle * Math.PI) / 180
              const left = 50 + PIN_RADIUS * Math.cos(rad)
              const top = 50 + PIN_RADIUS * Math.sin(rad)
              const seated = !t.ultimate || seat >= t.seatAt + 0.001
              return (
                <span
                  key={t.label}
                  className={`${styles.pin} ${seated ? styles.pinSeated : ""} ${
                    t.ultimate ? "" : styles.pinCore
                  }`}
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    // Counter-rotate so pins stay upright as the face turns.
                    transform: `translate(-50%, -50%) rotate(${-dialAngle}deg)`,
                  }}
                >
                  <span className={styles.pinDot} />
                </span>
              )
            })}
          </div>

          {/* The vault well at the centre. */}
          <div className={styles.well}>
            <div className={styles.wellPhotos} aria-hidden="true">
              {WELL_PHOTOS.map((f, i) => (
                <span className={styles.wellPhoto} key={f} data-i={i}>
                  <img
                    src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                    alt=""
                    draggable={false}
                    loading="eager"
                  />
                </span>
              ))}
              <span className={styles.wellGlass} />
            </div>
            <span className={styles.clasp} aria-hidden="true">
              <span className={styles.claspLock}>
                <Glyph name="lock" />
              </span>
              <span
                className={`${styles.claspLock} ${styles.claspSecond}`}
                data-on={isUlt}
              >
                <Glyph name="lock" />
              </span>
            </span>
            <span className={styles.capNum}>{gbLabel}</span>
            <span className={styles.capWord}>private storage</span>
          </div>

          {/* Fixed top index notch the face turns beneath. */}
          <span className={styles.topIndex} aria-hidden="true" />
        </div>

        {/* Detents — the accessible/tappable way to turn the dial. */}
        <div className={styles.detents}>
          <button
            className={`${styles.detent} ${!isUlt ? styles.detentActive : ""}`}
            onClick={() => selectTier("premium")}
            aria-pressed={!isUlt}
          >
            Premium
          </button>
          <span className={styles.detentArc} aria-hidden="true">
            {seatedCount}/8 armed
          </span>
          <button
            className={`${styles.detent} ${styles.detentUlt} ${
              isUlt ? styles.detentActive : ""
            }`}
            onClick={() => selectTier("ultimate")}
            aria-pressed={isUlt}
          >
            <Glyph name="spark" />
            Ultimate
          </button>
        </div>
      </section>

      {/* The eight tumblers named — seated checks light with the dial. */}
      <ul className={styles.legend}>
        {TUMBLERS.map((t) => {
          const seated = !t.ultimate || isUlt
          return (
            <li
              key={t.label}
              className={`${styles.leg} ${seated ? styles.legOn : ""}`}
            >
              <span className={styles.legMark}>
                {seated ? <Glyph name="check" /> : null}
              </span>
              <span className={styles.legLabel}>{t.label}</span>
              {t.ultimate && !isUlt && (
                <span className={styles.legTag}>Ultimate</span>
              )}
            </li>
          )
        })}
      </ul>

      {/* Pricing — yearly featured for the selected tier. */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${
            billing === "yearly" ? styles.yearActive : ""
          }`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            {isUlt ? "COMPLETE SET" : "CORE SET"} · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>
              {isUlt ? "Ultimate" : "Premium"} · Yearly
            </span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{monthlyOfYear}/mo, billed yearly</span>
            <span>
              {active.hasTrial
                ? `${active.trialDays}-day free trial`
                : "Billed annually"}
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            billing === "monthly" ? styles.monthActive : ""
          }`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <Glyph name="arrow" />
        </button>

        <p className={styles.fine}>
          {selectedPriceLine}. Auto-renews until canceled. Cancel anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default TumblerPaywall
